import React, { useState, useEffect } from 'react';
import { LogIn, AlertCircle } from 'lucide-react';
import { MODULE_META_CATALOG, setPageMeta } from '../../shared/meta';

interface UserAuthData {
  id: string;
  username: string;
  display_name: string;
  user_type: string;
  primary_email: string;
  organization: {
    id: string;
    code: string;
    name: string;
  };
}

interface LoginPageProps {
  onLoginSuccess: (token: string, user: UserAuthData) => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState<string>('safety_mgr_xyz');
  const [password, setPassword] = useState<string>('Password123!');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPersonaNote, setSelectedPersonaNote] = useState<string>('SMS Manager: Full SPI creation, baseline/threshold configuration, archive/unarchive, and company-wide delegation.');

  useEffect(() => {
    setPageMeta(MODULE_META_CATALOG['login']);
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: username.trim(),
          password,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Authentication failed.');
      }

      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('auth_user', JSON.stringify(data.user));
      onLoginSuccess(data.token, data.user);
    } catch (err: any) {
      setError(err.message || 'Unable to sign in.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page-wrapper">
      <div className="login-card">
        {/* Brand Header */}
        <div className="login-brand">
          <img
            src="/logo.png"
            alt="Luminix Logo"
            className="login-brand-img"
          />
          <span className="login-brand-title">Luminix</span>
        </div>

        <div className="login-header-text">
          <h2>Sign in to Luminix</h2>
          <p>Safety & Quality Management System Enterprise Portal</p>
        </div>

        {error && (
          <div className="login-error-alert">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleLogin} className="login-form">
          <div className="form-field">
            <label htmlFor="username">Username or Email</label>
            <input
              id="username"
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />
          </div>

          <div className="form-field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>

          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
            style={{ marginTop: '8px' }}
          >
            {loading ? (
              'Authenticating...'
            ) : (
              <>
                <LogIn size={18} />
                <span>Sign In</span>
              </>
            )}
          </button>
        </form>

        {/* Quick Testing Accounts */}
        <div className="quick-login-pills">
          <p>Quick Role Profile Switcher</p>
          
          <div style={{ marginTop: '12px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '6px' }}>
              Select Role Profile:
            </label>
            <select
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                backgroundColor: '#f8fafc',
                cursor: 'pointer'
              }}
              onChange={(e) => {
                const val = e.target.value;
                if (val) {
                  setUsername(`${val}_xyz`);
                  setPassword('Password123!');
                  setError(null);

                  if (val === 'safety_mgr') {
                    setSelectedPersonaNote('🎯 SMS Personeli: SPI oluşturabilir, 3-Sigma veya Specific Target limitlerini belirler, SPI arşivleyebilir/açabilir ve departman delegasyonları atayabilir.');
                  } else if (val === 'line_pilot') {
                    setSelectedPersonaNote('🎯 SPI Sorumlusu (Flight Ops): Sorumlu departmandır; hedef belirler, veri girişi yapabilir, bağlantılı Action ve Risk tanımlayabilir. Yeni SPI oluşturamaz.');
                  } else if (val === 'maint_tech') {
                    setSelectedPersonaNote('🎯 Data Provider (Teknik/Bakım): Veri girişi yapar; aylık/dönemsel değerleri kaydeder. Action/Risk tanımlayamaz, yeni SPI oluşturamaz.');
                  } else if (val === 'readonly_user') {
                    setSelectedPersonaNote('🎯 Stakeholder Departman (Kabin/Güvenlik): İzleyici paydaştır. SPI ve alert durumunu sadece görüntüler. Veri girişi yapamaz, risk ve aksiyon tanımlayamaz.');
                  } else if (val === 'regulatory_officer') {
                    setSelectedPersonaNote('🎯 Yetkisiz Departman Kullanıcısı: SMS veya Admin tarafından delegasyon rolü verilmedikçe ilgili departman SPI verilerini göremez.');
                  } else {
                    setSelectedPersonaNote('');
                  }
                }
              }}
            >
              <optgroup label="🎯 SPI Test Personas (Item 15: Easy Verification)">
                <option value="safety_mgr">★ 1. SMS Personeli (Creator & Admin - Ahmet Çetin)</option>
                <option value="line_pilot">★ 2. SPI Sorumlusu (Owner Dept - Flight Ops)</option>
                <option value="maint_tech">★ 3. Data Provider (Veri Sağlayıcı - Tech/Maint)</option>
                <option value="readonly_user">★ 4. Stakeholder (İzleyici Paydaş - View Only)</option>
                <option value="regulatory_officer">★ 5. Yetkisiz Departman Kullanıcısı (Non-Delegate)</option>
              </optgroup>
              <optgroup label="1. Administration & Governance">
                <option value="sysadmin">System Administrator (SYSTEM_ADMIN)</option>
                <option value="orgadmin">Organization Administrator (ORGANIZATION_ADMIN)</option>
              </optgroup>
              <optgroup label="2. Safety & Risk Management">
                <option value="safety_mgr">Safety Manager (SAFETY_MANAGER)</option>
                <option value="risk_mgr">Risk Manager (RISK_MANAGER)</option>
                <option value="investigation_mgr">Investigation Manager (INVESTIGATION_MANAGER)</option>
                <option value="report_admin">Report Administrator (REPORT_ADMIN)</option>
              </optgroup>
              <optgroup label="3. Quality & Compliance">
                <option value="quality_mgr">Quality Manager (QUALITY_MANAGER)</option>
                <option value="compliance_mgr">Compliance Manager (COMPLIANCE_MANAGER)</option>
                <option value="audit_mgr">Audit Manager (AUDIT_MANAGER)</option>
                <option value="regulatory_officer">Regulatory Reporting Officer (REGULATORY_REPORTING_OFFICER)</option>
              </optgroup>
              <optgroup label="4. Core Management Services">
                <option value="moc_mgr">MOC Manager (MOC_MANAGER)</option>
                <option value="doc_controller">Document Controller (DOCUMENT_CONTROLLER)</option>
                <option value="training_mgr">Training Manager (TRAINING_MANAGER)</option>
                <option value="task_mgr">Task & Actions Manager (TASK_MANAGER)</option>
              </optgroup>
              <optgroup label="5. Line Operations & Auditors">
                <option value="line_pilot">Line Captain Pilot (LINE_PILOT)</option>
                <option value="maint_tech">Maintenance Technician (MAINTENANCE_TECH)</option>
                <option value="readonly_user">Read-Only Auditor (READ_ONLY_USER)</option>
              </optgroup>
            </select>
          </div>

          {selectedPersonaNote && (
            <div style={{
              marginTop: '10px',
              padding: '10px 12px',
              borderRadius: '6px',
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              fontSize: '12px',
              color: '#1e40af',
              lineHeight: 1.4
            }}>
              {selectedPersonaNote}
            </div>
          )}

          <div style={{ marginTop: '14px', fontSize: '11px', color: '#64748b', textAlign: 'center' }}>
            All accounts use default password: <strong style={{ color: '#0f172a' }}>Password123!</strong>
          </div>
        </div>
      </div>
    </div>
  );
};
